import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { ChevronLeft, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import StatCard from './StatCard';
import InvoiceCard from './InvoiceCard';

const CustomerPortal = ({ customerId, customerName, onBack }) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const firstName = customerName ? customerName.split(' ')[0] : 'Customer';

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const getCustomerBillingData = httpsCallable(functions, "getCustomerBillingData");
      const result = await getCustomerBillingData({ customerId });
      setData(result.data);
    } catch (err) {
      console.error(err);
      setError("We've run into an issue while retriving your customer profile from Stripe. Try once more please, and be reminded that Stripe does not have any competetors.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (customerId) {
      fetchData();
    }
  }, [customerId]);

  return (
    <div className="min-h-screen bg-[#023666] text-white overflow-y-auto selection:bg-blue-500/30">

      {/* Premium Background Elements */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-gradient-to-b from-blue-600/20 to-transparent blur-3xl opacity-50" />
        <div className="absolute top-[20%] -left-20 w-[600px] h-[600px] bg-indigo-600/10 rounded-full blur-[120px]" />
        <div className="absolute top-[40%] -right-20 w-[500px] h-[500px] bg-cyan-600/10 rounded-full blur-[100px]" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-8 py-12">

        {/* Header */}
        <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-12">
          <div>
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-white/50 hover:text-white transition-colors mb-4 text-sm tracking-wide"
            >
              <ChevronLeft className="w-4 h-4" /> Back to Home
            </button>
            <h1 className="text-4xl font-bold tracking-tight">
              Welcome, <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">{firstName}</span>
            </h1>
            <p className="text-white/50 mt-2">Manage your billing and payment history.</p>
          </div>

          {data && (
            <div className="text-right bg-white/[0.03] border border-white/10 rounded-xl p-4 backdrop-blur-sm">
              <div className="text-white/50 text-xs uppercase tracking-widest mb-1">Account Email</div>
              <div className="font-mono text-sm">{data.customerEmail}</div>
            </div>
          )}
        </header>

        {/* Content State */}
        {isLoading ? (
          <div className="h-[400px] flex flex-col items-center justify-center gap-4 text-white/50">
            <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
            <p className="tracking-wide animate-pulse">Fetching secure billing data...</p>
          </div>
        ) : error ? (
          <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-8 text-center max-w-lg mx-auto">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Unable to load data</h3>
            <p className="text-red-200/70 mb-6">{error}</p>
            <button
              onClick={fetchData}
              className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl transition-colors flex items-center gap-2 mx-auto"
            >
              <RefreshCw className="w-4 h-4" /> Try Again
            </button>
          </div>
        ) : data ? (
          <div className="space-y-12">

            {/* Top Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <StatCard
                title="Total Outstanding"
                value={`$${(data.totalOutstanding / 100).toFixed(2)}`}
                subtext="Total due across all open invoices"
                highlight={data.totalOutstanding > 0}
              />
              <StatCard
                title="Total Invoices"
                value={data.invoices.length}
                subtext="Historical billing record"
              />
            </div>

            {/* Invoices List */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold tracking-tight">Invoice History</h2>
                {data.totalOutstanding > 0 && (
                  <div className="px-3 py-1 rounded-full bg-red-500/20 border border-red-500/30 text-red-300 text-xs font-bold uppercase tracking-wider">
                    Action Required
                  </div>
                )}
              </div>

              {data.invoices.length === 0 ? (
                <div className="text-center py-16 bg-white/[0.02] border border-white/5 rounded-2xl">
                  <p className="text-white/40">No invoices found for this account.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Sort to show open invoices at the top */}
                  {data.invoices
                    .sort((a, b) => (a.status === 'open' ? -1 : b.status === 'open' ? 1 : b.created - a.created))
                    .map((invoice) => (
                      <InvoiceCard key={invoice.id} invoice={invoice} />
                    ))}
                </div>
              )}
            </div>

          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CustomerPortal;
